import { ISbStoryData } from 'storyblok-js-client';

import { getStories } from '@monoimin/storyblok/getStories';

/**
 * Enhanced version of getFlatStories with better rate limiting and batching
 * Reduces concurrent requests during build time
 */
export async function getFlatStoriesOptimized<T>(
  params: { [k: string]: string | number | object },
  flatStories: ISbStoryData<T>[] = [],
  page = 1,
  batchDelay = 200 // ms delay between batches
): Promise<ISbStoryData<T>[]> {
  const {
    total,
    data: { stories },
  } = await getStories<T>({
    version: 'published',
    per_page: 100,
    page,
    ...params,
  });

  flatStories.push(...stories);
  
  if (flatStories.length < total) {
    // Add delay between requests to prevent overwhelming the API
    await new Promise(resolve => setTimeout(resolve, batchDelay));
    await getFlatStoriesOptimized(params, flatStories, page + 1, batchDelay);
  }

  return flatStories as ISbStoryData<T>[];
}

/**
 * Process multiple getFlatStories requests sequentially instead of concurrently
 * This prevents rate limiting during sitemap generation
 */
export async function batchGetFlatStories<T>(
  requests: Array<{ params: { [k: string]: string | number | object } }>,
  batchSize = 2,
  delayBetweenBatches = 500
): Promise<Array<ISbStoryData<T>[]>> {
  const results: Array<ISbStoryData<T>[]> = [];
  
  for (let i = 0; i < requests.length; i += batchSize) {
    const batch = requests.slice(i, i + batchSize);
    
    // Process batch concurrently but limit batch size
    const batchResults = await Promise.all(
      batch.map(({ params }) => getFlatStoriesOptimized<T>(params))
    );
    
    results.push(...batchResults);
    
    // Add delay between batches if not the last batch
    if (i + batchSize < requests.length) {
      await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
    }
  }
  
  return results;
}

/**
 * Sequential processing for sitemap generation to prevent rate limiting
 */
export async function generateSitemapDataSequentially<T>(
  locales: readonly string[],
  generateForLocale: (locale: string) => Promise<T>,
  delayBetweenLocales = 300
): Promise<T[]> {
  const results: T[] = [];
  
  for (let i = 0; i < locales.length; i++) {
    const locale = locales[i];
    const result = await generateForLocale(locale);
    results.push(result);
    
    // Add delay between locales if not the last one
    if (i < locales.length - 1) {
      await new Promise(resolve => setTimeout(resolve, delayBetweenLocales));
    }
  }
  
  return results;
}

export default getFlatStoriesOptimized;
